import { ChangeDetectionStrategy, Component, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { form, FormField, submit, required, email, minLength } from '@angular/forms/signals';
import { HlmButton } from '@spartan-ng/helm/button';
import { HlmInput } from '@spartan-ng/helm/input';
import { HlmLabel } from '@spartan-ng/helm/label';
import { HlmToaster } from '@spartan-ng/helm/sonner';
import { HlmSpinner } from '@spartan-ng/helm/spinner';
import { toast } from '@spartan-ng/brain/sonner';
import { LucideMail, LucideLock, LucideEye, LucideEyeOff, LucideAlertCircle } from '@lucide/angular';
import { AuthService } from '../../../core/services/auth.service';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    FormField,
    HlmButton,
    HlmInput,
    HlmLabel,
    HlmToaster,
    HlmSpinner,
    LucideMail,
    LucideLock,
    LucideEye,
    LucideEyeOff,
    LucideAlertCircle
  ],
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);

  // Variable de estado reactivo para mostrar/ocultar contraseña
  protected readonly showPassword = signal(false);

  // Estado de carga durante el proceso de autenticación
  protected readonly isLoading = signal(false);

  // Writable signal model para los datos del formulario (Evitar nulls)
  protected readonly loginModel = signal({
    email: '',
    password: '',
    rememberMe: false,
  });

  // Estructura y validación del formulario con Signal Forms
  protected readonly loginForm = form(this.loginModel, (s) => {
    required(s.email, { message: 'El correo electrónico es obligatorio' });
    email(s.email, { message: 'Por favor, introduce un correo electrónico válido' });
    required(s.password, { message: 'La contraseña es obligatoria' });
    minLength(s.password, 6, { message: 'La contraseña debe tener al menos 6 caracteres' });
  });

  // Conmutador de visibilidad de contraseña
  togglePasswordVisibility(): void {
    this.showPassword.update((val) => !val);
  }

  // Manejador del submit
  onSubmit(): void {
    submit(this.loginForm, async () => {
      this.isLoading.set(true);
      try {
        const { email, password } = this.loginModel();
        // Consumir el servicio de autenticación y esperar la respuesta
        await lastValueFrom(this.authService.login(email, password));
        
        // Disparar toast de éxito
        toast.success('Sesión administrativa iniciada correctamente');
        
        // Redirigir a Tasas de Cambio (el dashboard sigue accesible por URL)
        await this.router.navigate(['/rates']);
      } catch (err: any) {
        console.error('Error durante el login:', err);
        // Extraer el mensaje del error de negocio (code !== 1000) o de red
        const errMsg = err.message || err.error?.message || 'Correo electrónico o contraseña incorrectos';
        
        // Disparar toast de error
        toast.error(errMsg);
      } finally {
        this.isLoading.set(false);
      }
    });
  }
}
